import React from 'react';
import { WxbButton } from '../Button/Button';
import { WxbIcon } from '../Icon/Icon';
import type { GanttV5Model, GanttV5VisibleRows } from './types';

interface GanttV5SidebarProps {
  model: GanttV5Model;
  visibleRows: GanttV5VisibleRows;
  selectedTaskIds: ReadonlySet<string>;
  focusedTaskId: string | null;
  offsetPixels: number;
  onGroupToggle: (groupId: string, collapsed: boolean) => void;
  onTaskSelect: (taskId: string, additive: boolean) => void;
}

export const GanttV5Sidebar: React.FC<GanttV5SidebarProps> = ({
  model,
  visibleRows,
  selectedTaskIds,
  focusedTaskId,
  offsetPixels,
  onGroupToggle,
  onTaskSelect,
}) => {
  const rowsRef = React.useRef<HTMLDivElement>(null);

  React.useLayoutEffect(() => {
    rowsRef.current?.style.setProperty('--gantt-v5-sidebar-offset', `${offsetPixels}px`);
  }, [offsetPixels]);

  return (
    <aside className="wxb-gantt-v5-sidebar" aria-label="甘特图层级与任务">
      <div className="wxb-gantt-v5-sidebar__header">
        <span>层级 / 任务</span>
        <span>{model.rows.length}</span>
      </div>
      <div className="wxb-gantt-v5-sidebar__viewport">
        <div ref={rowsRef} className="wxb-gantt-v5-sidebar__rows">
          {visibleRows.rows.map((row, localIndex) => {
            const absoluteIndex = visibleRows.startIndex + localIndex;
            if (row.type === 'group') {
              return (
                <WxbButton
                  key={row.id}
                  variant="ghost"
                  size="sm"
                  className="wxb-gantt-v5-sidebar__row wxb-gantt-v5-sidebar__row--group"
                  aria-expanded={row.hasChildren ? row.expanded : undefined}
                  aria-level={row.depth + 1}
                  onClick={() => row.hasChildren && onGroupToggle(row.id, row.expanded)}
                >
                  <span className={`wxb-gantt-v5-sidebar__indent wxb-gantt-v5-sidebar__indent--${Math.min(4, row.depth)}`} />
                  {row.hasChildren ? (
                    <WxbIcon name={row.expanded ? 'chevron-down' : 'chevron-right'} size={14} />
                  ) : (
                    <span className="wxb-gantt-v5-sidebar__icon-spacer" />
                  )}
                  <span className="wxb-gantt-v5-sidebar__label">{row.label}</span>
                  <span className="wxb-gantt-v5-sidebar__count">{row.descendantTaskCount}</span>
                </WxbButton>
              );
            }
            const selected = Boolean(row.taskId && selectedTaskIds.has(row.taskId));
            return (
              <WxbButton
                key={row.id}
                variant="ghost"
                size="sm"
                className={`wxb-gantt-v5-sidebar__row wxb-gantt-v5-sidebar__row--task ${selected ? 'is-selected' : ''}`}
                aria-current={focusedTaskId === row.taskId ? 'true' : undefined}
                aria-posinset={absoluteIndex + 1}
                aria-setsize={model.rows.length}
                onClick={(event) => row.taskId && onTaskSelect(row.taskId, event.metaKey || event.ctrlKey || event.shiftKey)}
              >
                <span className={`wxb-gantt-v5-sidebar__indent wxb-gantt-v5-sidebar__indent--${Math.min(4, row.depth)}`} />
                <WxbIcon name="recipe" size={14} />
                <span className="wxb-gantt-v5-sidebar__label">{row.label}</span>
              </WxbButton>
            );
          })}
        </div>
      </div>
    </aside>
  );
};
